import { useAppSelector } from '../store/hooks';

/**
 * Hook to check if current user has a specific permission
 * @param permission - Permission string (e.g., "conversations:create")
 * @returns boolean - true if user has permission
 */
export const usePermission = (permission: string): boolean => {
  const { user } = useAppSelector(state => state.auth);

  // Super admins have all permissions
  if (user?.role === 'SUPER_ADMIN') {
    return true;
  }

  // Check if permission exists in user's permissions array
  return user?.permissions?.includes(permission) || false;
};

/**
 * Hook to check if user has ANY of the specified permissions (OR logic)
 * @param permissions - Array of permission strings
 * @returns boolean - true if user has at least one permission
 */
export const useAnyPermission = (permissions: string[]): boolean => {
  const { user } = useAppSelector(state => state.auth);

  if (user?.role === 'SUPER_ADMIN') {
    return true;
  }

  return permissions.some(perm => user?.permissions?.includes(perm));
};

/**
 * Hook to check if user has ALL of the specified permissions (AND logic)
 * @param permissions - Array of permission strings
 * @returns boolean - true if user has all permissions
 */
export const useAllPermissions = (permissions: string[]): boolean => {
  const { user } = useAppSelector(state => state.auth);

  if (user?.role === 'SUPER_ADMIN') {
    return true;
  }

  return permissions.every(perm => user?.permissions?.includes(perm));
};

/**
 * Hook to get all user permissions
 * @returns string[] - Array of all user permissions
 */
export const useUserPermissions = (): string[] => {
  const { user } = useAppSelector(state => state.auth);
  return user?.permissions || [];
};

/**
 * Hook to check permissions loading state
 * @returns boolean - true if permissions are loaded
 */
export const usePermissionsLoaded = (): boolean => {
  const permissionsLoaded = useAppSelector(state => state.auth.permissionsLoaded);
  return permissionsLoaded;
};